import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { stripeService } from '../../../services/stripeService';

const InvoiceHistory = ({ invoices, compact = false, onViewAll }) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const statusConfig = {
    paid: { label: 'Paid', color: 'text-success', bgColor: 'bg-success/10', icon: 'CheckCircle' },
    open: { label: 'Open', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'Clock' },
    draft: { label: 'Draft', color: 'text-muted-foreground', bgColor: 'bg-muted', icon: 'FileText' },
    void: { label: 'Void', color: 'text-error', bgColor: 'bg-error/10', icon: 'XCircle' },
    uncollectible: { label: 'Uncollectible', color: 'text-error', bgColor: 'bg-error/10', icon: 'AlertCircle' }
  };

  const sortedInvoices = [...(invoices || [])]?.sort((a, b) => {
    if (sortBy === 'date') {
      const dateA = new Date(a?.created_at);
      const dateB = new Date(b?.created_at);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    }
    if (sortBy === 'amount') {
      return sortOrder === 'desc' ? b?.amount - a?.amount : a?.amount - b?.amount;
    }
    return 0;
  });

  const displayedInvoices = compact ? sortedInvoices?.slice(0, 5) : sortedInvoices;

  const handleDownload = (invoice) => {
    if (invoice?.invoice_pdf_url) {
      window.open(invoice?.invoice_pdf_url, '_blank');
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Invoice History</h3>
        </div>
        {!compact && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              iconName={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'}
              iconPosition="left"
              onClick={() => toggleSort('date')}
            >
              Date
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName={sortOrder === 'desc' ? 'ArrowDown' : 'ArrowUp'}
              iconPosition="left"
              onClick={() => toggleSort('amount')}
            >
              Amount
            </Button>
          </div>
        )}
      </div>
      {displayedInvoices?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
            <Icon name="FileText" size={24} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground text-sm">No invoices yet</p>
          <p className="text-muted-foreground text-xs mt-1">Your billing history will appear here</p>
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Invoice</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedInvoices?.map((invoice) => {
                  const status = statusConfig?.[invoice?.status] || statusConfig?.draft;
                  return (
                    <tr key={invoice?.id} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Icon name="FileText" size={16} className="text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground">
                            {invoice?.invoice_number || `INV-${invoice?.id?.slice(0, 8)}`}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-muted-foreground">
                          {stripeService?.formatDate(invoice?.created_at)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm font-medium text-foreground">
                          {stripeService?.formatAmount(invoice?.amount, invoice?.currency)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${status?.bgColor}`}>
                          <Icon name={status?.icon} size={12} className={status?.color} />
                          <span className={`text-xs font-medium ${status?.color}`}>{status?.label}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Download"
                          onClick={() => handleDownload(invoice)}
                          disabled={!invoice?.invoice_pdf_url}
                        >
                          Download
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {displayedInvoices?.map((invoice) => {
              const status = statusConfig?.[invoice?.status] || statusConfig?.draft;
              return (
                <div key={invoice?.id} className="border border-border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="FileText" size={16} className="text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">
                          {invoice?.invoice_number || `INV-${invoice?.id?.slice(0, 8)}`}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {stripeService?.formatDate(invoice?.created_at)}
                      </span>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full ${status?.bgColor}`}>
                      <Icon name={status?.icon} size={12} className={status?.color} />
                      <span className={`text-xs font-medium ${status?.color}`}>{status?.label}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-foreground">
                      {stripeService?.formatAmount(invoice?.amount, invoice?.currency)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => handleDownload(invoice)}
                      disabled={!invoice?.invoice_pdf_url}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {compact && invoices?.length > 5 && (
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              onClick={onViewAll}
              className="mt-4"
            >
              View All Invoices ({invoices?.length})
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default InvoiceHistory;